import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MessageCircle, CalendarDays, Cog, Fuel } from "lucide-react";
import { formatBRL, waLink } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RentalSection = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/rentals/vehicles`)
      .then((res) => setVehicles(res.data))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && vehicles.length === 0) return null;

  return (
    <section id="locacao" className="py-24 sm:py-32 relative overflow-hidden" data-testid="rental-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(31,206,109,0.07),transparent_55%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="text-xs font-mono2 tracking-widest uppercase text-emerald-400 mb-3">02 — Locação</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white">
            Alugue por <span className="text-emerald-400">diária</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl">
            Precisa de um carro por alguns dias? Dirija um Selected sem compromisso de compra.
            Retirada na loja, contrato digital e suporte durante todo o período.
          </p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2].map((i) => <div key={i} className="h-80 rounded-2xl bg-[#131620] animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rental-grid">
            {vehicles.map((v, i) => (
              <motion.article
                key={v.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group relative rounded-2xl overflow-hidden bg-[#131620] border border-slate-800/60 hover:border-emerald-500/40 transition-colors duration-500"
                data-testid={`rental-card-${v.id}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={v.imagem} alt={v.nome} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#131620] to-transparent" />
                  <span className="absolute bottom-4 right-4 glass rounded-full px-4 py-1.5 font-display font-bold text-emerald-400" data-testid={`rental-price-${v.id}`}>
                    {formatBRL(v.diaria)}<span className="text-xs font-normal text-slate-300">/dia</span>
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-white">{v.nome}</h3>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-emerald-500" />{v.ano}</span>
                    <span className="flex items-center gap-1.5"><Cog className="w-3.5 h-3.5 text-emerald-500" />{v.cambio}</span>
                    <span className="flex items-center gap-1.5"><Fuel className="w-3.5 h-3.5 text-emerald-500" />{v.combustivel}</span>
                  </div>
                  <a
                    href={waLink(`Olá! Quero alugar o ${v.nome} (${v.ano}) por diária de ${formatBRL(v.diaria)}. Quais datas estão livres?`)}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`rental-cta-${v.id}`}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-bold px-4 py-3 rounded-full transition-colors duration-300"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Quero alugar
                  </a>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
